/**
 * caseEncounters.js — WHICH ENCOUNTER IS THE PATIENT STANDING IN?
 * v242.170.0 · FU-REPORT-ENCOUNTER-GUESS-1
 *
 * ★★★ THE MEASUREMENT THAT REWROTE THE ROW. The row said the patient app picks the
 * encounter to SUBMIT THE PRE-VISIT REPORT TO with `encounters.slice(-1)[0]`.
 * Measured 2026-09-08: it does not. `submitPreVisitReport` is called with a
 * NUMBER taken from the case pointer, and the three `slice(-1)[0]` sites feed
 * `aiAPI.generateBridgeQuestions`, which uses the encounter ROW to read and write
 * the question cache in `encounters.interval_data`. So the report and the
 * questions were resolved by two different rules on one screen.
 *
 * ★★★ AND THAT IS WHY IT MATTERS, because today the two rules usually agree.
 * Both server paths return encounters `order=encounter_number.asc`, so the last
 * array element IS the highest-numbered row. The highest-numbered row is not the
 * pointer: `finalize_encounter` seals N and inserts N+1, and the API says in as
 * many words that it does NOT touch `current_encounter` — "a transition changes
 * which cassettes exist, not where the patient is standing". Only the Patient
 * Acknowledgment Gate advances the pointer. BETWEEN A FINALIZE AND AN
 * ACKNOWLEDGMENT THE TWO RULES NAME DIFFERENT ROWS, and in that window the app
 * wrote the bridge questions onto N+1's `interval_data` while the report went to
 * N — and every reader that resolves by the pointer, the MD panel's pane
 * included, looks at N and finds no questions.
 *
 * ★★★ NINE SITES, FOUR ANSWERS. Before this file: `slice(-1)[0]?.id` (×3),
 * `currentEncounter || 1` (×3), `currentEncounter || current_encounter || 2` (×3)
 * — and that last fallback is a typed constant wearing a default, the same shape
 * the MD panel named FU-SIG-ENCOUNTER-2-1. A case with no pointer does not have
 * an encounter 2 because someone wrote a 2.
 *
 * ★★ THE RULE, AND IT IS THE MD PANEL'S. `resolveEncounterNum` there is
 * `currentEncounter || current_encounter || 1`, resolved against
 * `encounter_number`. This file is that, plus one repair: when the case carries
 * no pointer at all, the fallback is the HIGHEST-NUMBERED ROW THAT EXISTS rather
 * than a literal — 1 and 2 are both guesses, and the rows are not.
 *
 * ★ A NULL ROW IS A REAL STATE, NOT AN ERROR TO PAPER OVER. If the pointer names
 *   an encounter this client has not loaded (a stale cache mid-transition),
 *   `currentEncounter()` returns null and `currentEncounterId()` returns null.
 *   Sending no encounter id is what the app already did when a case had no
 *   encounters, and the server treats it as "no cache to read".
 *
 * ★ NO IMPORTS — `scripts/check-encounter-owner.mjs` loads this in node to run the
 *   rules against constructed cases, so the arm tests an OUTCOME and not a shape.
 */

const num = (e) => {
  const n = e?.encounter_number ?? e?.encounterNumber
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

/** Every encounter on the case, in ENCOUNTER-NUMBER order — never array order. */
export function encountersOf(caseData) {
  const rows = Array.isArray(caseData?.encounters) ? caseData.encounters : []
  return [...rows].sort((a, b) => (num(a) ?? 0) - (num(b) ?? 0))
}

/**
 * The number of the encounter the patient is standing in: the case's own
 * pointer, else the highest-numbered row that exists, else 1.
 */
export function currentEncounterNumber(caseData) {
  const pointer = caseData?.currentEncounter ?? caseData?.current_encounter
  if (typeof pointer === 'number' && Number.isFinite(pointer) && pointer > 0) return pointer
  const rows = encountersOf(caseData)
  for (let i = rows.length - 1; i >= 0; i--) {
    const n = num(rows[i])
    if (n != null) return n
  }
  return 1
}

/** The ROW that number names, or null when the case does not carry it. */
export function currentEncounter(caseData) {
  const want = currentEncounterNumber(caseData)
  return encountersOf(caseData).find((e) => num(e) === want) || null
}

/** Its id, for anything addressing an encounter by row rather than by number. */
export function currentEncounterId(caseData) {
  return currentEncounter(caseData)?.id ?? null
}

/**
 * The tolerant read: the encounter the pointer names, else the newest row.
 *
 * ★★★ A READ MAY FALL BACK TO THE NEWEST ROW; A WRITE MAY NOT. Showing a
 * patient the newest care plan when the pointer names a row this client has not
 * loaded is a reasonable degradation — they see something true about their case,
 * one visit off at worst. WRITING to that row is not: it puts this visit's
 * answers on another visit, where the reader that resolved by the pointer will
 * never find them and the reader who opens that pane will see them attributed to
 * a visit they did not belong to. So the two are separate functions with
 * different names, rather than one function with a flag.
 *
 * ★ This is `hooks/useActiveCarePlan.js`'s `resolveActiveEncounter`, which had
 *   the rule RIGHT the whole time. The Deliver screen simply never called it —
 *   the correct answer was already in the app, in a file that screen did not
 *   import. That hook now delegates here rather than keeping a fourth copy.
 */
export function activeEncounter(caseData) {
  const exact = currentEncounter(caseData)
  if (exact) return exact
  const rows = encountersOf(caseData)
  return rows.length ? rows[rows.length - 1] : null
}
